// iter 144d - Admin Partner Applications moderation page.
//
// Route: /admin/partner-applications
//
// Lists all applications, filterable by status. Click a row to open the
// detail drawer with approve / reject actions.

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, X, Check, Ban, ExternalLink, FileText } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const REJECT_REASONS = [
  'Does not meet quality standards',
  'Incomplete documentation',
  'Service not currently needed',
  'Duplicate application',
  'Other',
];

const CATEGORIES = [
  'Legal', 'Healthcare', 'Insurance', 'Financial', 'Real Estate',
  'Lifestyle', 'Vehicle Advisory', 'Other', 'Technology', 'Banking', 'Housing',
];

const SERVICE_TO_CATEGORY = {
  'Immigration Attorney': 'Legal',
  'Real Estate Attorney': 'Legal',
  'General Practice Attorney': 'Legal',
  'Healthcare Provider - Doctor or Specialist': 'Healthcare',
  'Healthcare Provider - Dentist': 'Healthcare',
  'Health Insurance Broker': 'Insurance',
  'International Tax Accountant or CPA': 'Financial',
  'Financial Advisor': 'Financial',
  'Real Estate Agent or Broker': 'Real Estate',
  'Property Manager': 'Real Estate',
  'Relocation Consultant': 'Lifestyle',
  'Vehicle Purchase Advisory': 'Vehicle Advisory',
  'Moving and Shipping Services': 'Lifestyle',
  'Language School': 'Lifestyle',
  'Other': 'Other',
};

const FILTER_TABS = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
];

const StatusBadge = ({ status }) => {
  const tones = {
    pending:  { bg: '#FFF1D6', fg: '#8C5A14', label: 'PENDING' },
    approved: { bg: '#D7F0E0', fg: '#1A7F4F', label: 'APPROVED' },
    rejected: { bg: '#F8D7D7', fg: '#9F2222', label: 'REJECTED' },
  };
  const t = tones[status] || tones.pending;
  return (
    <span
      className="inline-block px-2.5 py-1 font-mono uppercase text-[9px] tracking-[0.18em] font-semibold rounded"
      style={{ background: t.bg, color: t.fg }}
    >
      {t.label}
    </span>
  );
};

const fmtDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', timeZone: 'America/Chicago'});
  } catch { return iso; }
};

const ApproveModal = ({ application, onClose, onApproved }) => {
  const [form, setForm] = useState({
    name: application?.business_name || '',
    category: SERVICE_TO_CATEGORY[application?.service_type] || 'Other',
    cities: (application?.cities || '').split(',').map(s => s.trim()).filter(Boolean),
    description: application?.description || '',
    contact_email: application?.contact_email || '',
    photo_url: '',
    languages: application?.languages || [],
    credentials: '',
    fee_structure: application?.fee_structure || '',
    expat_experience: application?.expat_clients_count || '',
  });
  const [cityInput, setCityInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const addCity = () => {
    const v = cityInput.trim();
    if (!v) return;
    if (!form.cities.includes(v)) setForm((p) => ({ ...p, cities: [...p.cities, v] }));
    setCityInput('');
  };
  const removeCity = (c) =>
    setForm((p) => ({ ...p, cities: p.cities.filter((x) => x !== c) }));

  const confirm = async () => {
    setSubmitting(true);
    setError('');
    try {
      const r = await fetch(
        `${API}/api/admin/partner-applications/${application.id}/approve`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        }
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err?.detail || `HTTP ${r.status}`);
      }
      onApproved();
    } catch (e) {
      setError(e.message || 'Approval failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0A1F2E]/60 flex items-start justify-center overflow-y-auto py-10 px-4">
      <div
        className="bg-white border border-[#0A1F2E]/10 rounded-2xl max-w-2xl w-full p-7"
        data-testid="approve-partner-modal"
      >
        <div className="flex justify-between items-start mb-5">
          <div>
            <p className="font-mono uppercase text-[10px] tracking-[0.20em] text-[#2CA8A0] mb-1">
              Approve & Add to Directory
            </p>
            <h2 className="font-display italic text-2xl text-[#0A1F2E]">
              {application.applicant_name}
            </h2>
          </div>
          <button onClick={onClose} className="text-[#5A6B73] hover:text-[#0A1F2E]" data-testid="approve-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-xs text-[#5A6B73] mb-5">
          All fields pre-filled from the application. Edit anything you want before
          confirming. This creates a <code>vetted_professional</code> row in the
          partner directory.
        </p>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#5A6B73] block mb-1">Partner name</label>
            <input
              data-testid="approve-name"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              className="w-full border border-[#d5d0c8] rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#5A6B73] block mb-1">Category</label>
            <select
              data-testid="approve-category"
              value={form.category}
              onChange={(e) => set('category', e.target.value)}
              className="w-full border border-[#d5d0c8] rounded-lg px-3 py-2 text-sm"
            >
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#5A6B73] block mb-1">Cities</label>
            <div className="flex gap-2 mb-1.5">
              <input
                data-testid="approve-city-input"
                value={cityInput}
                onChange={(e) => setCityInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCity())}
                placeholder="Add a city and press Enter"
                className="flex-1 border border-[#d5d0c8] rounded-lg px-3 py-2 text-sm"
              />
              <button type="button" onClick={addCity} className="px-3 py-2 text-xs bg-[#0A1F2E] text-white rounded-lg">Add</button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {form.cities.map((c) => (
                <span key={c} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-[#F4ECD8] rounded">
                  {c}
                  <button type="button" onClick={() => removeCity(c)} className="text-[#5A6B73] hover:text-[#C0392B]">×</button>
                </span>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#5A6B73] block mb-1">Description</label>
            <textarea
              data-testid="approve-description"
              rows={4}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              className="w-full border border-[#d5d0c8] rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#5A6B73] block mb-1">Contact email (for managed messaging)</label>
            <input
              data-testid="approve-contact-email"
              type="email"
              value={form.contact_email}
              onChange={(e) => set('contact_email', e.target.value)}
              className="w-full border border-[#d5d0c8] rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#5A6B73] block mb-1">Fee structure</label>
            <textarea
              data-testid="approve-fee-structure"
              rows={2}
              value={form.fee_structure}
              onChange={(e) => set('fee_structure', e.target.value)}
              className="w-full border border-[#d5d0c8] rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[#5A6B73] block mb-1">Expat experience</label>
              <input
                data-testid="approve-expat"
                value={form.expat_experience}
                onChange={(e) => set('expat_experience', e.target.value)}
                className="w-full border border-[#d5d0c8] rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[#5A6B73] block mb-1">Credentials (one-line summary)</label>
              <input
                data-testid="approve-credentials"
                value={form.credentials}
                onChange={(e) => set('credentials', e.target.value)}
                className="w-full border border-[#d5d0c8] rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#5A6B73] block mb-1">Photo URL (optional)</label>
            <input
              data-testid="approve-photo-url"
              value={form.photo_url}
              onChange={(e) => set('photo_url', e.target.value)}
              placeholder="https://res.cloudinary.com/..."
              className="w-full border border-[#d5d0c8] rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>

        {error && <p className="mt-3 text-sm text-[#C0392B]">{error}</p>}

        <div className="mt-5 flex gap-3 justify-end">
          <button onClick={onClose} className="px-5 py-2.5 text-sm border border-[#d5d0c8] rounded-full">Cancel</button>
          <button
            data-testid="approve-confirm-btn"
            onClick={confirm}
            disabled={submitting || !form.name.trim()}
            className="px-6 py-2.5 text-sm font-mono uppercase tracking-wider bg-[#2CA8A0] text-white rounded-full hover:brightness-95 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {submitting ? 'Approving…' : 'Confirm & Add to Directory'}
          </button>
        </div>
      </div>
    </div>
  );
};

const RejectModal = ({ application, onClose, onRejected }) => {
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const confirm = async () => {
    if (!reason) { setError('Please select a reason'); return; }
    setSubmitting(true);
    setError('');
    try {
      const r = await fetch(
        `${API}/api/admin/partner-applications/${application.id}/reject`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason, message }),
        }
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err?.detail || `HTTP ${r.status}`);
      }
      onRejected();
    } catch (e) {
      setError(e.message || 'Rejection failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0A1F2E]/60 flex items-start justify-center overflow-y-auto py-10 px-4">
      <div
        className="bg-white border border-[#0A1F2E]/10 rounded-2xl max-w-lg w-full p-7"
        data-testid="reject-partner-modal"
      >
        <div className="flex justify-between items-start mb-5">
          <div>
            <p className="font-mono uppercase text-[10px] tracking-[0.20em] text-[#C0392B] mb-1">Reject Application</p>
            <h2 className="font-display italic text-2xl text-[#0A1F2E]">{application.applicant_name}</h2>
          </div>
          <button onClick={onClose} className="text-[#5A6B73] hover:text-[#0A1F2E]" data-testid="reject-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#5A6B73] block mb-1">Reason *</label>
            <select
              data-testid="reject-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full border border-[#d5d0c8] rounded-lg px-3 py-2 text-sm"
            >
              <option value="">- Select a reason -</option>
              {REJECT_REASONS.map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#5A6B73] block mb-1">
              Add a personal note to the applicant (optional)
            </label>
            <textarea
              data-testid="reject-message"
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full border border-[#d5d0c8] rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>

        {error && <p className="mt-3 text-sm text-[#C0392B]">{error}</p>}

        <div className="mt-5 flex gap-3 justify-end">
          <button onClick={onClose} className="px-5 py-2.5 text-sm border border-[#d5d0c8] rounded-full">Cancel</button>
          <button
            data-testid="reject-confirm-btn"
            onClick={confirm}
            disabled={submitting}
            className="px-6 py-2.5 text-sm font-mono uppercase tracking-wider bg-[#C0392B] text-white rounded-full hover:brightness-95 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
            {submitting ? 'Rejecting…' : 'Confirm Rejection'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DetailDrawer = ({ application, onClose, onChanged }) => {
  const [showApprove, setShowApprove] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [internalNotes, setInternalNotes] = useState(application.internal_notes || '');
  const [savingNotes, setSavingNotes] = useState(false);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await fetch(`${API}/api/admin/partner-applications/${application.id}/internal-notes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ internal_notes: internalNotes }),
      });
    } finally {
      setSavingNotes(false);
    }
  };

  const Row = ({ label, children }) => (
    <div className="py-2.5 border-b border-[#0A1F2E]/5">
      <p className="text-[10px] font-mono uppercase tracking-wider text-[#5A6B73] mb-1">{label}</p>
      <div className="text-sm text-[#0A1F2E]">{children || '-'}</div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-40 bg-[#0A1F2E]/40">
      <aside
        data-testid="application-detail-drawer"
        className="absolute right-0 top-0 h-full w-full md:w-[640px] bg-[#F9F6EF] overflow-y-auto shadow-2xl"
      >
        <div className="sticky top-0 bg-white border-b border-[#0A1F2E]/10 px-6 py-4 flex justify-between items-center">
          <div>
            <p className="font-mono uppercase text-[10px] tracking-[0.20em] text-[#5A6B73] mb-1">
              Application · {application.id.slice(0, 16)}…
            </p>
            <h2 className="font-display italic text-xl text-[#0A1F2E]">
              {application.applicant_name} - {application.business_name}
            </h2>
          </div>
          <button onClick={onClose} data-testid="drawer-close" className="text-[#5A6B73] hover:text-[#0A1F2E]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5">
          <div className="flex items-center gap-3 mb-5">
            <StatusBadge status={application.status} />
            <span className="text-xs text-[#5A6B73]">Submitted {fmtDate(application.submitted_at)}</span>
            {application.reviewed_at && (
              <span className="text-xs text-[#5A6B73]">· Reviewed {fmtDate(application.reviewed_at)}</span>
            )}
          </div>

          <Row label="Service type">{application.service_type}</Row>
          <Row label="Cities">{application.cities}</Row>
          <Row label="Languages">{(application.languages || []).join(', ')}</Row>
          <Row label="Email"><a href={`mailto:${application.contact_email}`} className="text-[#2CA8A0]">{application.contact_email}</a></Row>
          <Row label="Phone">{application.phone}</Row>
          <Row label="Website">
            {application.website ? (
              <a href={application.website} target="_blank" rel="noopener noreferrer" className="text-[#2CA8A0] inline-flex items-center gap-1">
                {application.website} <ExternalLink className="w-3 h-3" />
              </a>
            ) : '-'}
          </Row>
          <Row label="Expat clients">{application.expat_clients_count}</Row>
          <Row label="Description">{application.description}</Row>
          <Row label="Fee structure">{application.fee_structure}</Row>

          {application.rejection_reason && (
            <Row label="Rejection reason">{application.rejection_reason}</Row>
          )}
          {application.rejection_note && (
            <Row label="Rejection note">{application.rejection_note}</Row>
          )}
          {application.created_partner_id && (
            <Row label="Created partner">
              <code className="text-xs">{application.created_partner_id}</code>
            </Row>
          )}

          {/* Documents */}
          <p className="mt-6 mb-2 text-[10px] font-mono uppercase tracking-wider text-[#1A5C6E]">Documents</p>
          <div className="grid grid-cols-1 gap-2">
            {(application.credential_files || []).map((url, i) => (
              <a
                key={`cred-${i}`}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`doc-credential-${i}`}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border border-[#0A1F2E]/10 rounded-lg hover:border-[#2CA8A0]"
              >
                <FileText className="w-4 h-4 text-[#5A6B73]" />
                Credential {i + 1}
                <ExternalLink className="w-3 h-3 ml-auto text-[#5A6B73]" />
              </a>
            ))}
            {application.id_file && (
              <a
                href={application.id_file}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="doc-id"
                className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border border-[#0A1F2E]/10 rounded-lg hover:border-[#2CA8A0]"
              >
                <FileText className="w-4 h-4 text-[#5A6B73]" />
                Government ID
                <ExternalLink className="w-3 h-3 ml-auto text-[#5A6B73]" />
              </a>
            )}
            {(application.supporting_files || []).map((url, i) => (
              <a
                key={`supp-${i}`}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`doc-supporting-${i}`}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border border-[#0A1F2E]/10 rounded-lg hover:border-[#2CA8A0]"
              >
                <FileText className="w-4 h-4 text-[#5A6B73]" />
                Supporting {i + 1}
                <ExternalLink className="w-3 h-3 ml-auto text-[#5A6B73]" />
              </a>
            ))}
          </div>

          {/* Internal notes */}
          <p className="mt-6 mb-2 text-[10px] font-mono uppercase tracking-wider text-[#1A5C6E]">
            Internal notes (admin only - never shown to applicant)
          </p>
          <textarea
            data-testid="internal-notes"
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            onBlur={saveNotes}
            rows={3}
            className="w-full border border-[#d5d0c8] rounded-lg px-3 py-2 text-sm bg-white"
          />
          {savingNotes && <p className="text-xs text-[#5A6B73] mt-1">Saving…</p>}

          {/* Actions */}
          {application.status === 'pending' && (
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="action-approve"
                onClick={() => setShowApprove(true)}
                className="flex-1 px-6 py-3 text-sm font-mono uppercase tracking-wider bg-[#2CA8A0] text-white rounded-full hover:brightness-95 inline-flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" />
                Approve and Add to Directory
              </button>
              <button
                data-testid="action-reject"
                onClick={() => setShowReject(true)}
                className="flex-1 px-6 py-3 text-sm font-mono uppercase tracking-wider bg-[#C0392B] text-white rounded-full hover:brightness-95 inline-flex items-center justify-center gap-2"
              >
                <Ban className="w-4 h-4" />
                Reject Application
              </button>
            </div>
          )}
        </div>
      </aside>

      {showApprove && (
        <ApproveModal
          application={application}
          onClose={() => setShowApprove(false)}
          onApproved={() => { setShowApprove(false); onChanged(); }}
        />
      )}
      {showReject && (
        <RejectModal
          application={application}
          onClose={() => setShowReject(false)}
          onRejected={() => { setShowReject(false); onChanged(); }}
        />
      )}
    </div>
  );
};

export default function AdminPartnerApplications() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('pending');
  const [selected, setSelected] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(
        `${API}/api/admin/partner-applications?status=${filter}`,
        { credentials: 'include' }
      );
      if (r.status === 401) {
        navigate('/admin/login');
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setApplications(data.applications || []);
    } catch (e) {
      setError(e.message || 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }, [filter, navigate]);

  useEffect(() => { reload(); }, [reload]);

  const openDetail = async (app) => {
    // Fetch fresh detail in case fields were updated since list load.
    try {
      const r = await fetch(`${API}/api/admin/partner-applications/${app.id}`, {
        credentials: 'include',
      });
      if (r.ok) {
        const fresh = await r.json();
        setSelected(fresh);
      } else {
        setSelected(app);
      }
    } catch {
      setSelected(app);
    }
  };

  const counts = {
    all: applications.length,
  };

  return (
    <div className="min-h-screen bg-[#F9F6EF]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <p className="font-mono uppercase text-[10px] tracking-[0.20em] text-[#1A5C6E] mb-1">
              Mundalo Admin
            </p>
            <h1 className="font-display italic text-3xl md:text-4xl text-[#0A1F2E]">
              Partner Applications
            </h1>
          </div>
          <a
            href="/admin/dashboard"
            className="text-xs font-mono uppercase tracking-wider text-[#5A6B73] hover:text-[#2CA8A0]"
          >
            ← Back to dashboard
          </a>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {FILTER_TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`filter-${t.id}`}
              onClick={() => setFilter(t.id)}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-wider rounded-full transition-colors ${
                filter === t.id
                  ? 'bg-[#0A1F2E] text-white'
                  : 'bg-white text-[#5A6B73] border border-[#d5d0c8] hover:border-[#2CA8A0]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white border border-[#0A1F2E]/8 rounded-2xl overflow-hidden">
          {loading && (
            <div className="p-10 text-center text-[#5A6B73] inline-flex items-center justify-center gap-2 w-full">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Loading applications…</span>
            </div>
          )}
          {error && (
            <div className="p-10 text-center text-[#C0392B]" data-testid="applications-error">
              {error}
            </div>
          )}
          {!loading && !error && applications.length === 0 && (
            <div className="p-10 text-center text-[#5A6B73]" data-testid="applications-empty">
              No applications in this filter.
            </div>
          )}
          {!loading && !error && applications.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-[#F4ECD8] text-[#5A6B73]">
                <tr>
                  <th className="text-left px-4 py-3 font-mono uppercase text-[10px] tracking-wider">Submitted</th>
                  <th className="text-left px-4 py-3 font-mono uppercase text-[10px] tracking-wider">Applicant</th>
                  <th className="text-left px-4 py-3 font-mono uppercase text-[10px] tracking-wider">Business</th>
                  <th className="text-left px-4 py-3 font-mono uppercase text-[10px] tracking-wider">Service</th>
                  <th className="text-left px-4 py-3 font-mono uppercase text-[10px] tracking-wider">Cities</th>
                  <th className="text-left px-4 py-3 font-mono uppercase text-[10px] tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 font-mono uppercase text-[10px] tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {applications.map((a) => (
                  <tr
                    key={a.id}
                    data-testid={`application-row-${a.id}`}
                    onClick={() => openDetail(a)}
                    className="border-t border-[#0A1F2E]/5 hover:bg-[#F9F6EF] cursor-pointer"
                  >
                    <td className="px-4 py-3 text-[#5A6B73] text-xs whitespace-nowrap">{fmtDate(a.submitted_at)}</td>
                    <td className="px-4 py-3 text-[#0A1F2E]">{a.applicant_name}</td>
                    <td className="px-4 py-3 text-[#0A1F2E]">{a.business_name}</td>
                    <td className="px-4 py-3 text-[#5A6B73] text-xs">{a.service_type}</td>
                    <td className="px-4 py-3 text-[#5A6B73] text-xs">{a.cities}</td>
                    <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                    <td className="px-4 py-3">
                      <button
                        data-testid={`view-${a.id}`}
                        onClick={(e) => { e.stopPropagation(); openDetail(a); }}
                        className="text-xs font-mono uppercase tracking-wider text-[#2CA8A0] hover:underline"
                      >
                        View →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selected && (
        <DetailDrawer
          application={selected}
          onClose={() => setSelected(null)}
          onChanged={() => { setSelected(null); reload(); }}
        />
      )}
    </div>
  );
}
